import { bookActions, loadingActions, shareActions } from '@/actions';
import { Button, Fab } from '@/components/elements/forms/buttons';
import { HeaderButtonIdentifier } from '@/components/elements/HeaderButton/HeaderButton.types';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import ProgressTracker from '@/components/elements/ProgressTracker';
import { StatusMessage } from '@/components/elements/StatusMessage';
import { ChevronIcon, LogoIcon, PlusAltIcon, SuccessRingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import CheckoutViewLayout from '@/components/layouts/CheckoutViewLayout';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import { resolveEmployeeFromUrl } from '@/features/booking-flow/resolveEmployeeFromUrl';
import { parseSelection } from '@/features/booking-flow/urlState';
import { getBookableEmployeeListItems } from '@/features/booking-selection/views/SelectEmployeeList.helpers';
import { NewCustomerDiscountBanner } from '@/components/modules/banner';
import { EmployeeBox } from '@/components/modules/employee';
import { CancelBooking, ModalContent, ModalDialog, ReadMoreModal, ModalHeader } from '@/components/modules/modals';
import {
  EmployeePicker,
  LastMinuteWeekPicker,
  MonthPicker,
  ServicePicker,
  WeekPicker,
} from '@/components/modules/pages/bookService';
import { ROUTE_BUILDER } from '@/constants/pages';
import { useBookingFlowRedesign } from '@/features/booking-flow/useBookingFlowRedesign';
import { useWeekpickerKillSwitch } from '@/features/weekpicker/useWeekpickerKillSwitch';
import EntryError from '@/features/booking-widget/EntryError';
import { getContainmentMode } from '@/helpers/containmentPolicy';
import {
  activeUsersCount,
  allowMarketing,
  capitalizeFirstLetter,
  exportIdSlug,
  findEmployee,
  getBookingStartingPoint,
  getCampaignOfferTotal,
  getEmployeesWhoPerformServices,
  getServiceCampaignIfAny,
  getServiceDuration,
  getServicePrice,
  getTotalPrice,
  isEmpty,
  isMobile,
  isServer,
  isSistaminuten as isSistaminutenInstance,
  limitString,
  performedServices,
  restoreBodyScroll,
  salonTracking,
  saveBodyScroll,
  server,
  serviceIsExtraTimeAddOn,
  servicesWithExtraTimeAddOns,
  setBookingStartingPoint,
  shouldShowFrom,
  showPrices,
  startOfDay,
  trackMpEvent,
  trackPage,
  trackPageCio,
  url,
} from '@/helpers';
import { BOOKING_FLOW_STEP_BACK_LABEL_KEYS } from '@/features/booking-flow/constants';
import { isForwardExitPathname, trackCalendarPopToStep } from '@/features/booking-flow/tracking';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { hasDealCampagin } from '@/helpers/placeHelperTS';
import { slug } from '@/helpers/urlTs';
import { LoginContext } from '@/hooks/useLogin';
import { _t, __ } from '@/locale';
import NotFound from '@/pages/404';
import React, { Fragment, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import QRCode from 'react-qr-code';
import { connect } from 'react-redux';
import { Link, Redirect, useHistory, withRouter } from 'react-router-dom';
import PickTimePage from './PickTimePage';

const themed = (bokadirektClasses, sistaminutenClasses, isSistaminuten) => {
  return isSistaminuten ? sistaminutenClasses : bokadirektClasses;
};

class Book extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showServices: false,
      toCheckout: __('selectHour'),
      changeEmpl: false,
      changeEmplModal: false,
      activeEmployees: props.place ? activeUsersCount(props.place.employees, props) : {},
      showMonthView: false,
      is404: props.is404 || false,
      shared: {
        showModal: false,
      },
      hasAddedRecommendedService: false,
      bottomStickyNavbarRefHeight: undefined,
    };

    this.removeService = this.removeService.bind(this);
    this.hidePopup = this.hidePopup.bind(this);
    this.bottomStickyNavbarRef = React.createRef();
  }

  seo() {
    const service = this.props.services && this.props.services[0] ? this.props.services[0] : null;
    const place = this.props.place;
    const serviceName = service && service.name ? service.name : '';
    const salonName = place && place.about && place.about.name ? place.about.name : '';
    const salonAddress =
      (place?.contact?.address?.city || '') +
      (place?.contact?.address?.district ? ' ' + place.contact.address.district : '');
    const title = limitString(__('book') + ' ' + serviceName + ' - ' + salonName + ' - Bokadirekt', 60);
    const description = limitString(
      service && service.about && service.about.description
        ? service.about.description
        : __('seo.serviceDescription') +
            serviceName +
            ' ' +
            __('seo.serviceAt') +
            ' ' +
            salonName +
            ', ' +
            salonAddress,
      155,
    );
    const serviceUrl =
      url.getBaseUrl() +
      'boka-tjanst/' +
      place.about.slug +
      '-' +
      place.id +
      '/' +
      service?.about?.slug +
      '-' +
      service.id;
    const placeUrl = url.getBaseUrl() + 'places/' + place.about.slug + '-' + place.id;
    const isNoIndex = true; // All pages will be unindexed
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={placeUrl} />
        <meta property="og:url" content={serviceUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content="Bokadirekt" />
        <meta property="og:locale" content="sv_SE" />
        {!isSistaminutenInstance() && <meta name="twitter:site" content="@bokadirekt" />}
        <meta name="robots" content={isNoIndex ? 'noindex' : 'index, follow'} />
      </Helmet>
    );
  }

  setGclidQueryParamsToSession() {
    const queryParams = new URLSearchParams(window.location?.search);
    const gclid = queryParams.has('gclid') ? queryParams.get('gclid') : undefined;
    if (gclid) {
      sessionStorage.setItem('gclid', gclid);
    }
  }

  componentDidMount() {
    const { dispatch, place, services, employee } = this.props || {};

    this.setGclidQueryParamsToSession();

    const serviceSlugId = exportIdSlug(this.props.match.params.serviceSlugId);
    // Both bodies read change-time from the store, so it mirrors the entry before either renders.
    dispatch(bookActions.changeBookingTime(this.entryChangeTimeBookingId()));
    if (this.checkRetry()) {
      const evProps = bookActions.getTrackingProps();
      delete evProps.transactionProps;

      trackMpEvent('error_shown', {
        error_message: __('retryBooking'),
        error_type: 'retry',
        ...evProps,
      });
    }

    const shared = url.returnGetParameter('shared');
    const serviceIds = url.returnGetParameter('sIds')?.split('-') || [];
    // The one precedence rule between the two link mechanisms: a URL that names its selection
    // in the flow's own `ss` is read by PickTimePage, and the share bootstrap must not also
    // append `sIds` underneath it. Without `ss`, share links run exactly as before. The new body
    // has to be the one rendering for the rule to hold at all: nothing on the legacy body reads
    // `ss`, so suppressing the share bootstrap there would drop the shared selection outright.
    const hasFlowSelection = this.rendersNewPickTime() && Boolean(parseSelection(this.props.location?.search ?? '').ss);
    const addsSharedServices = shared && serviceIds.length > 0 && !hasFlowSelection;

    if (
      !place ||
      !services ||
      !services.filter((service) => parseInt(service.id, 10) === parseInt(serviceSlugId.id, 10)).length
    ) {
      if (addsSharedServices) {
        this.addSharedServices();
      } else {
        // There is no service . take the one from url and call the server for details
        this.getService(serviceSlugId);
      }
    } else {
      this.setState({ waiting: false });
      if (
        employee &&
        !(this.props.location && this.props.location.state && this.props.location.state.retry) &&
        !this.rendersNewPickTime()
      ) {
        setTimeout(() => {
          const elem = document.querySelector('div.calendar');
          if (elem) {
            elem.parentNode.scrollTop = elem.offsetTop;
          } else {
            window.scrollTo(0, 0);
          }
        }, 1000);
      } else {
        window.scrollTo(0, 0);
      }

      dispatch(loadingActions.show('loadHours'));

      salonTracking(place);

      if (this.sendMarketingData()) {
        trackPage();
        trackPageCio('booking_step_choose_time', this.getCioTrackingProps());
      }

      if (!getBookingStartingPoint()) {
        setBookingStartingPoint('booking_step_choose_time');
      }

      const activeEmployees = activeUsersCount(place.employees, this.props);
      const { employeeCount } = activeEmployees;
      this.setState({ changeEmpl: !isMobile() && employeeCount > 1 ? true : false, activeEmployees });
      this.selectEmployeeIfOne(activeEmployees);

      // What the legacy pickers do on their own mount and `addService` does on the fetch branch:
      // derive `book.campaigns` from the selection. The new body's only other writer is the slot
      // pick, so a store that arrives with the selection already in it (an SSR seed) would price
      // a deal or last-minute link at full price until then.
      if (this.rendersNewPickTime()) {
        dispatch(bookActions.setCampaigns(services, place.campaigns, this.props.time?.timestamp || startOfDay()));
      }

      if (addsSharedServices) {
        this.addSharedServices();
      }

      if (!bookingFlowTrackingSession.isActive() && !this.entryChangeTimeBookingId()) {
        bookingFlowTrackingSession.initiate(place, this.props.user, 'booking_step_choose_time');
      }
    }
  }

  rendersNewPickTime() {
    return Boolean(this.props.newPickTimeEnabled);
  }

  /**
   * The booking this calendar moves, as stamped on the entry by the booking page's "Ändra tid"
   * link. The entry, not the store, decides change-time: history keeps the stamp across back,
   * forward and reload, while a new tab, a copied link or any other way in arrives without it —
   * where an id left in the store would turn a new booking into a move of the old one.
   */
  entryChangeTimeBookingId() {
    return this.props.location?.state?.changeTimeBookingId;
  }

  selectEmployeeIfOne(activeEmployees) {
    const { dispatch, employee } = this.props || {};

    const { employeeCount, users } = activeEmployees;
    const selectedEmployee = this.getEmployee(employeeCount, users);
    if (employeeCount === 1 || (employee && employee === selectedEmployee.id)) {
      dispatch(bookActions.pickEmployee(selectedEmployee.id, selectedEmployee.about.priceListId));
      dispatch(bookActions.keepEmployee());
    }
    // The new body resolves `sr` against the bookable list, as its fetch branch does: a store
    // that arrives with the selection already in it (an SSR seed) gets no other correction, and
    // the legacy lookup below would keep an employee who cannot take the service.
    if (this.rendersNewPickTime()) {
      this.pickUrlEmployee(this.props.place, this.props.services || []);
      return;
    }
    const oldEmployeeId = url.returnGetParameter('sr');
    if (oldEmployeeId) {
      const employeeData = this.props.place.employees.filter((e) => e.about.externalId === oldEmployeeId).pop();
      if (employeeData && employeeData.id && employeeData.about && employeeData.about.priceListId) {
        dispatch(bookActions.pickEmployee(employeeData.id, employeeData.about.priceListId));
        dispatch(bookActions.keepEmployee());
      }
    }
  }

  /**
   * Picks the employee named by `sr` on the new body: after the fetch branch's `clearBook`, and
   * on a mount whose store already holds the selection. The new body owns the employee pick, so
   * it skips `selectEmployeeIfOne` on every fetch (componentDidUpdate) — leaving this the only
   * place a cold entry (a flow entry that picked the employee before the service, a reload, a
   * deep link) can rebuild it. Without it the picked employee silently becomes "Valfri
   * utövare", and the calendar fetches any-employee availability. Only a matched externalId is
   * restored: `sr=0` needs no pick, an absent `book.employee` already means any employee here.
   * Change-time then overrides it with the booking's own employee, in the fetch branch below.
   */
  pickUrlEmployee(place, services) {
    if (!this.rendersNewPickTime()) return;
    const { dispatch, location } = this.props || {};
    // The same list the panel and /valj-utovare offer, so `sr` cannot pick someone the visitor
    // has no way to pick or switch away from: an employee who is no longer bookable, or who does
    // not perform the whole selection, would scope the availability fetch to an empty calendar.
    const { bookableEmployees } = getBookableEmployeeListItems(
      place,
      services.map((service) => service.id).filter((id) => id != null),
    );
    const { matched } = resolveEmployeeFromUrl(parseSelection(location?.search ?? '').sr, bookableEmployees);
    if (!matched) return;
    dispatch(bookActions.pickEmployee(matched.id, matched.about?.priceListId));
    dispatch(bookActions.keepEmployee());
  }

  getService(serviceSlugId) {
    const { dispatch, place, appliedBundle, employee: employeeId, flags } = this.props || {};
    const changeTimeBookingId = this.entryChangeTimeBookingId();

    this.setState({ waiting: true });
    dispatch(loadingActions.show('loadHours'));

    const isSistaminuten = url.returnGetParameter('sistaminuten') !== undefined;
    const dealId = url.returnGetParameter('dealId');

    let serverUrl = `/service/${serviceSlugId.id}/${serviceSlugId.slug}`;

    if (isSistaminuten) {
      serverUrl += '?sistaminuten=1';
    } else if (dealId) {
      serverUrl += '?dealId=' + dealId;
    }

    // Don't have the details or we have other service/place set
    server.request
      .get(serverUrl)
      .then((result) => {
        if (result.data && result.data.redirect && !isServer) {
          return (window.location = result.data.redirect);
        }

        if (result.data && result.data.service) {
          dispatch(bookActions.removeEmployee());
          dispatch(bookActions.clearBook());
          dispatch(bookActions.addPlace(result.data.place));
          dispatch(bookActions.addService(result.data.service, result.data.place));

          if (appliedBundle) {
            dispatch(bookActions.applyBundle(appliedBundle));
          }

          this.pickUrlEmployee(result.data.place, [result.data.service]);

          if (changeTimeBookingId) {
            const employee = findEmployee(employeeId, result.data.place.employees);

            dispatch(bookActions.changeBookingTime(changeTimeBookingId));
            dispatch(bookActions.pickEmployee(employee?.id || 0, employee?.about?.priceListId));
            dispatch(bookActions.keepEmployee());
          }

          salonTracking(place);

          if (this.sendMarketingData(result.data.service)) {
            trackPage();
            trackPageCio('booking_step_choose_time', this.getCioTrackingProps(result.data.place, result.data.service));
          }
          if (!bookingFlowTrackingSession.isActive() && !changeTimeBookingId) {
            bookingFlowTrackingSession.initiate(result.data.place, this.props.user, 'booking_step_choose_time');
          }
        }

        dispatch(loadingActions.hide());
        this.setState({ waiting: false });
      })
      .catch((error) => {
        dispatch(loadingActions.hide());
        this.setState({ is404: true, waiting: false });
      });
  }

  sendMarketingData(service) {
    let services = [];
    if (service?.id) {
      services.push(service);
    } else {
      services = this.props.services || [];
    }
    return allowMarketing(services);
  }

  getCioTrackingProps(place, service) {
    if (!place) {
      place = this.props.place || {};
    }

    let serviceName = service?.name;
    if (!serviceName) {
      serviceName = (this.props.services || []).map((item) => item.name).join(', ');
    }

    const cioData = {
      merchant_id: place?.id,
      merchant_name: place?.about?.name,
      service_name: serviceName,
    };
    if (url.returnGetParameter('sistaminuten') !== undefined) {
      cioData.is_sistaminuten = true;
    }

    return cioData;
  }

  componentDidUpdate(prevProps, prevState) {
    const { place, availability } = this.props;
    const context = this.context; // LoginContext

    if (prevProps.availability !== availability && place) {
      const activeEmployees = activeUsersCount(place.employees, this.props);
      const { employeeCount } = activeEmployees;

      this.setState({ changeEmpl: !isMobile() && employeeCount > 1 ? true : false, activeEmployees });
      // The new body makes and owns the employee pick; re-running this on every fetch would
      // override its "any employee" with the only employee that has availability.
      if (!this.rendersNewPickTime()) {
        this.selectEmployeeIfOne(activeEmployees);
      }
    }

    const currentIDs = this.props.services ? this.props.services.map((service) => service.id) : [];
    const oldIds = prevProps.services ? prevProps.services.map((service) => service.id) : [];
    if (currentIDs.join('-') !== oldIds.join('-')) {
      this.setState({ showServices: false });
    }

    /**
     * recaulculate bottom sticky navbar height on open/close
     */
    if (prevState.isOpen !== this.state.isOpen) {
      this.setState({
        bottomStickyNavbarRefHeight: this.bottomStickyNavbarRef?.current?.offsetHeight,
      });
    }
  }

  addRecommendedServices = (e) => {
    bookingFlowTrackingSession.addService('initiated');
    this.setState({ showRecommended: true, hasAddedRecommendedService: false });
  };

  closeRecommendedServices = (e) => {
    const { services } = this.props;

    const service_ids = services.map((s) => s.id.toString());

    bookingFlowTrackingSession.setProperties({ service_ids });

    if (this.state.hasAddedRecommendedService) {
      bookingFlowTrackingSession.addService('success');
    } else {
      bookingFlowTrackingSession.addService('cancel');
    }

    this.setState({ showRecommended: false });
  };

  addService = (e) => {
    const { dispatch, employee } = this.props;
    const selectedEmployee = this.getEmployee();
    if (employee) {
      dispatch(bookActions.pickEmployee(selectedEmployee.id, selectedEmployee.about.priceListId));
      dispatch(bookActions.keepEmployee());
    }

    this.setState({ showServices: true });

    bookingFlowTrackingSession.addService('initiated');

    if (this.sendMarketingData()) {
      trackPageCio('booking_choose_service_prompt', this.getCioTrackingProps());
    }
  };

  removeService(serviceId) {
    const { dispatch, time, services, employee, place } = this.props;
    dispatch(bookActions.popService(serviceId, time && time.timestamp, services, employee, place));

    const service_ids = services.map((service) => service.id.toString()).filter((id) => id !== serviceId.toString());
    bookingFlowTrackingSession.setProperties({ service_ids });
    bookingFlowTrackingSession.removedService();
  }

  getEmployeeName() {
    const { employee, place } = this.props;
    let performerName = __('anyEmployee');
    place.employees.forEach((people) => {
      if (employee === people.id) {
        performerName = people.about.name;
      }
    });
    return performerName;
  }

  hidePopup(aborted = false) {
    this.setState({ showServices: false });

    if (aborted) {
      bookingFlowTrackingSession.addService('cancel');
    }
  }

  handleCheckoutClick = (e) => {};

  openBottom = (e) => {
    const { isOpen } = this.state;

    this.setState({ isOpen: !isOpen });

    /**
     * Save height of bottom sticky navbar to avoid layout shift
     * when opening/closing the bottom sticky navbar
     */
    if (!isOpen && !this.state.bottomStickyNavbarRefHeight) {
      this.setState({ bottomStickyNavbarRefHeight: this.bottomStickyNavbarRef?.current?.offsetHeight });
    }
  };

  checkRetry = () => {
    let retry = null;
    if (!isServer) {
      if (this.props.location.state && this.props.location.state.retry) {
        window.history.pushState(null, '');

        retry = (
          <div className="my-4">
            <StatusMessage
              type="error"
              message={
                this.props.location.state.retryKlarna
                  ? __('retryBookingKlarna')
                  : this.props.location.state.retryQliro
                  ? __('retryBookingQliro')
                  : __('retryBooking')
              }
            />
          </div>
        );
      }
    }

    return retry;
  };

  handleBackClick = (e) => {
    e.preventDefault();
    this.trackBackButton();
    if (this.props.history.length === 0) {
    } else {
      this.props.history.goBack();
    }
  };

  trackBackButton() {}

  // For alert
  promptAlert = (e) => {
    this.setState({ showAlert: true });
  };

  handleClose = (e) => {
    this.setState({ showAlert: false });
  };

  editEmployee = (e) => {
    if (isMobile()) {
      if (this.sendMarketingData()) {
        trackPageCio('booking_choose_person_prompt', this.getCioTrackingProps());
      }
    }
    saveBodyScroll();
    this.setState({ changeEmplModal: true });
  };
  hideEmployees = (e) => {
    this.setState({ changeEmpl: false, changeEmplModal: false }, () => {
      restoreBodyScroll();
    });
  };

  getHeader(type = 'pickTime', hideSteps = false) {
    return (
      <div className="steps-container">
        <h2 className="font-semibold" style={{ textAlign: 'center' }}>
          {__(type)}
        </h2>
        {!hideSteps && this.getSteps()}
      </div>
    );
  }

  hasDiscount() {
    if (isServer) {
      return null;
    }
    const { place } = this.props;
    const offer = sessionStorage.getItem('ClickedOffer');
    if (offer && place && parseInt(place.id, 10) === parseInt(offer, 10)) {
      return (
        <div className="my-4">
          <NewCustomerDiscountBanner onRemove={this.removeDiscount} variant="simple" />
        </div>
      );
    }
    return null;
  }

  removeDiscount = (e) => {
    sessionStorage.removeItem('ClickedOffer');
    this.setState({ render: !this.state.render });
  };

  getEmployee(count, employeeIds) {
    const { place, services, employee } = this.props;
    let selectedEmployee = null;

    const selected =
      place && place.employees
        ? place.employees.filter(
            (theEmpl) => theEmpl.id === employee || (count === 1 && employeeIds.indexOf(theEmpl.id) !== -1),
          )
        : [];

    if (selected.length > 0) {
      selectedEmployee = selected[0];
    }

    if (!selectedEmployee) {
      selectedEmployee = { id: 0, about: { name: __('anyEmployee'), avatar: '', isBookable: true } };
      selectedEmployee.services = performedServices(services, place.employees);
    }

    return selectedEmployee;
  }

  getLastWorkingDays() {
    const { place } = this.props;
    const { users } = this.state.activeEmployees || {};
    const { bookAhead: placeBookAhead } = place.about.book || 16;
    const lastDays = { last: 0, bookAhead: 0, byEmployee: {} };
    place.employees.forEach((employee) => {
      if (users?.indexOf(employee.id) !== -1) {
        const { settings = {} } = employee.about || {};
        const lastWorkingDay = settings.lastDay || 0;
        const bookAhead = settings.bookAhead || placeBookAhead;
        if (lastDays.last < lastWorkingDay) {
          lastDays.last = lastWorkingDay;
        }
        if (lastDays.bookAhead < bookAhead) {
          lastDays.bookAhead = bookAhead;
        }
        lastDays.byEmployee[employee.id] = { lastWorkingDay, bookAhead };
      }
    });

    return lastDays;
  }
  selectedServices(selectedServices, inModal = false) {
    // Needed props
    const { place, priceId } = this.props;
    const isSistaminuten = isSistaminutenInstance() || Boolean(place?.lastMinuteDiscount);
    const servicesIds = selectedServices.map((service) => service.id);
    const { services, mapServiceWithExtraTimeAddOns } = servicesWithExtraTimeAddOns(selectedServices);
    const servicesHtml = [];
    let start = Infinity,
      end = 0;
    // Loop each service
    services.forEach((service, key) => {
      const { campaignService, campaign } = getServiceCampaignIfAny(service, place.campaigns, servicesIds);
      const extraTimeAddOn = mapServiceWithExtraTimeAddOns[service?.id];
      let price = getServicePrice(service, priceId, place);
      if (extraTimeAddOn?.id) {
        price = getTotalPrice([service, extraTimeAddOn], place, priceId, {}, 0, 1, true);
      }
      if (campaignService) {
        let offerPrice = getServicePrice(service, priceId, place, campaignService);
        if (extraTimeAddOn?.id) {
          offerPrice = getTotalPrice([service, extraTimeAddOn], place, priceId, {}, 0, 1, true, place.campaigns);
        }

        if (offerPrice && offerPrice !== price) {
          price = (
            <span className={themed('text-information', 'text-green-sm', isSistaminuten)}>
              {offerPrice + ' '}
              <s>{'(ord. ' + price.replace(__('from'), '') + ')'}</s>
            </span>
          );
        }

        start = Math.min(start, campaign.startDate);
        end = Math.max(end, campaign.endDate);
      } else if (campaign) {
        // if no campaignService then just products are discounted
        if (campaign?.publicationType === 2) {
          start = Math.min(start, campaign.publicationStart);
          end = Math.max(end, campaign.publicationEnd);
        }
      }

      const serviceDuration = getServiceDuration(service, priceId, 1, extraTimeAddOn?.id ? extraTimeAddOn : undefined);
      const showTooltip = service.about && service.about.description;
      const { isAddOn } = service?.about?.settings || {};
      const shouldShowCancel =
        isAddOn || services.filter((s) => s.about && s.about.settings && !s.about.settings.isAddOn).length > 1;

      const hasAvailableTimeslots = !isEmpty(
        (this.props.availability && this.props.availability.calendarTimeSlots) || {},
      );

      servicesHtml.push(
        <div key={key} className="flex flex-col pb-4">
          <div className="flex items-start">
            {inModal && (
              <div className="mr-2 mt-2 h-4 w-4">
                {!isAddOn && <SuccessRingIcon className="text-black-600 h-full w-full" />}
              </div>
            )}
            <div className="flex-1 pr-2">
              <h6>{capitalizeFirstLetter(service.name)}</h6>
              <div className="text-black-600 text-sm">
                <span>{serviceDuration}</span>
                {price && serviceDuration && ', '}
                {price && <span>{price}</span>}
                {showTooltip && (
                  <>
                    &nbsp;&middot;&nbsp;
                    <ReadMoreModal
                      modalProps={{
                        title: service.name,
                        info: service.about.description,
                        handleOnOpen: () => {
                          bookingFlowTrackingSession.viewServiceDetails('initiated', hasAvailableTimeslots);
                        },
                        handleOnClose: () => {
                          bookingFlowTrackingSession.viewServiceDetails('cancel', hasAvailableTimeslots);
                        },
                      }}
                    />
                  </>
                )}
              </div>
            </div>
            {shouldShowCancel && (
              <Fab
                icon={<Icon variant="close" />}
                onClick={() => this.removeService(service.id)}
                size="md"
                variant="link"
              />
            )}
          </div>
        </div>,
      );
    });

    return { servicesHtml, start, end };
  }

  selectedProducts(inModal = false) {
    const { place } = this.props;
    const products = place?.campaigns?.map?.((campaign) => campaign?.products || []).flat() || [];

    return products.map((product, key) => {
      const isDiscounted =
        product.originalPriceLabel && product.priceLabel && product.originalPriceLabel !== product.priceLabel;
      return (
        <div key={key} className="flex flex-col pb-4">
          <div className="flex items-start">
            {inModal && (
              <div className="mr-2 mt-2 h-4 w-4">
                <SuccessRingIcon className="text-black-600 h-full w-full" />
              </div>
            )}
            <div className="flex-1 pr-2">
              <h6>{capitalizeFirstLetter(product.name)}</h6>
              <div className="text-black-600 text-sm">
                {isDiscounted ? (
                  <span className={'text-information'}>
                    {product.priceLabel + ' '}
                    <s>{'(ord. ' + product.originalPriceLabel.replace(__('from'), '') + ')'}</s>
                  </span>
                ) : (
                  <span>{product.priceLabel}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  appendRecommendedService(service) {
    const { dispatch, time, services, employee, place } = this.props;
    const isSistaminuten = isSistaminutenInstance() || Boolean(place?.lastMinuteDiscount);
    const dealCampaign = this.getDealCampaign();

    dispatch(bookActions.appendExtraService(service, services));
    dispatch(
      bookActions.getAvailability({
        services: [...services, service],
        date: time && time.timestamp,
        employee: employee || 0,
        isSistaminuten,
        dealId: dealCampaign?.id,
      }),
    );

    this.setState({ hasAddedRecommendedService: true });
  }

  recommendedServices(selectedEmployee) {
    const { services = [], place } = this.props;
    const mainService =
      services.filter((s) => !(s && s.about && s.about.settings && s.about.settings.isAddOn)).pop() || {};
    const { serviceCount } = servicesWithExtraTimeAddOns(services);
    const { addOnServices } = mainService.extra || {};
    const serviceIds = services.map((se) => se.id);
    const remainingAddOn = addOnServices
      .filter((s) => serviceIds.indexOf(s.id) === -1)
      .filter((s) => !serviceIsExtraTimeAddOn(s))
      .sort((a, b) => {
        return ('' + a.name).localeCompare(b.name);
      });
    const { servicesHtml } = this.selectedServices(services, true);
    return (
      <ModalDialog
        isOpen={true}
        appElement={document.getElementById('root')}
        onRequestClose={this.closeRecommendedServices}>
        <ModalContent size="lg">
          <div>
            <span className="mr-3 mt-3 flex justify-end">
              <Fab icon={<Icon variant="close" />} onClick={this.closeRecommendedServices} size="md" variant="link" />
            </span>
          </div>
          <h2 className="mb-4 px-6 font-semibold">{__('service', { count: serviceCount })}</h2>
          <div className="flex flex-col items-start justify-between px-6 pb-4 md:flex-row">
            <div className="w-full flex-1">{servicesHtml}</div>
            <div className="flex w-full justify-end md:w-auto">
              <Button onClick={this.closeRecommendedServices} size="sm" className="ml-auto">
                {__('continue')}&nbsp;
                <span className="text-sm font-normal lowercase">({__('service', { count: serviceCount })})</span>
              </Button>
            </div>
          </div>
          {remainingAddOn && remainingAddOn.length > 0 && (
            <div
              className="services p-6"
              style={{ margin: 0, borderTop: 1, borderTopColor: '#F4F4F6', borderTopStyle: 'solid' }}>
              <h2 className="mb-4 font-semibold">{__('recommendedServices')}</h2>
              <ul>
                {remainingAddOn.map((recommendedService, k) => {
                  let priceListId = null;
                  if (selectedEmployee && selectedEmployee.about) {
                    priceListId = selectedEmployee.about.priceListId;
                  } else {
                    const performing = getEmployeesWhoPerformServices(place.employees, [mainService.id]);
                    if (performing.length === 1 && performing[0].about) {
                      priceListId = performing[0].about.priceListId;
                    }
                  }
                  const serviceDuration = getServiceDuration(recommendedService, priceListId);
                  let price = getServicePrice(recommendedService, priceListId, place);
                  const name = isMobile()
                    ? capitalizeFirstLetter(recommendedService.name.split('.').join('. '))
                    : capitalizeFirstLetter(recommendedService.name);

                  return (
                    <li
                      key={k}
                      onClick={() => this.appendRecommendedService(recommendedService)}
                      className="!last-of-type:border-none hover:bg-black-100 !flex cursor-pointer items-start justify-between rounded-lg p-[10px]">
                      <div className="flex flex-1 flex-col pr-2">
                        <span>{name}</span>
                        <span className="text-black-600 flex items-center text-sm">
                          {serviceDuration}
                          {price && serviceDuration && ', '}
                          {price && price}
                        </span>
                      </div>
                      <Button variant="secondary" size="sm" title={__('add') + ' ' + name}>
                        {__('add')}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </ModalContent>
      </ModalDialog>
    );
  }

  getParams() {
    const {
      place,
      services,
      priceId,
      campaigns,
      show,
      loadSource,
      time = {},
      changeTimeBookingId,
      dealCampaign,
    } = this.props;
    const isSistaminuten = isSistaminutenInstance() || Boolean(place?.lastMinuteDiscount);
    const { waiting, changeEmpl, activeEmployees, changeEmplModal } = this.state;
    let selectedTimestamp = parseInt((time.timestamp || startOfDay()) / 1000, 10);
    if (waiting === false && (!place || !services || !services.length)) {
      return { redirect: true };
    }
    if (!isServer && waiting !== false) {
      return {
        loader: <LoadingPlaceHolder text={__('loadingHours')} />,
      };
    }

    const { employeeCount, users } = activeEmployees || {};
    const selectedEmployee = this.getEmployee(employeeCount, users);
    const prompt = this.state.showAlert ? (
      <CancelBooking
        place={place}
        cancel={this.handleClose}
        steps="-1"
        source={Boolean(this.props.location.state && this.props.location.state.fromLanding)}
      />
    ) : null;

    const pickedProducts = (campaigns || []).flatMap((campaign) => campaign.products ?? []);
    const [productPrices, productPricesWithoutCampaigns] = [
      pickedProducts.map((p) => ({ price: p.price })),
      pickedProducts.filter((p) => p.originalPrice !== undefined).map((p) => ({ price: p.originalPrice })),
    ];

    let total = getTotalPrice(
      services,
      place,
      priceId,
      {},
      0,
      1,
      true,
      [],
      false,
      undefined,
      undefined,
      undefined,
      productPricesWithoutCampaigns,
    );
    let offerTotal = getTotalPrice(
      services,
      place,
      priceId,
      {},
      0,
      1,
      true,
      campaigns,
      false,
      undefined,
      undefined,
      undefined,
      productPrices,
    );

    const isPriceVariable =
      shouldShowFrom(services, place) && total !== __('variablePrice') && total !== __('freePrice');
    const shouldShowPrices = showPrices(place, services);

    if (offerTotal !== total) {
      offerTotal = getCampaignOfferTotal(offerTotal, total);
      total = (
        <span className={`text-sm font-semibold ${themed('text-information', 'text-highlight', isSistaminuten)}`}>
          {offerTotal + ' '}
          <s className="text-black-600">{'(ord. ' + total + ')'}</s>
        </span>
      );
    }
    const cost = this.getCost(total, isPriceVariable);
    const campaignTotal = shouldShowPrices ? (
      <span className="mb-4 flex items-center">
        {/* fake radio button */}
        <div
          className={`mr-3 h-[16px] w-[16px] min-w-[16px] rounded-full p-1 ${themed(
            'bg-information-50',
            'bg-primary-50',
            isSistaminuten,
          )}`}>
          <div
            className={`h-full w-full rounded-full ${themed('bg-information', 'bg-highlight', isSistaminuten)}`}></div>
        </div>
        {/* # fake radio button */}
        <span className="flex-1 text-sm">
          {__('campaignPrice')}&nbsp;{isPriceVariable ? __('from').toLowerCase() : ''}&nbsp;{total}
        </span>
      </span>
    ) : null;
    const servicesRelated = this.pickedServices(selectedEmployee);
    const pickedServicesHtml = servicesRelated.html;
    const campaignStart = servicesRelated.campaignStart;
    const campaignEnd = servicesRelated.campaignEnd;
    const hasCampaign =
      campaignStart && campaignEnd && selectedTimestamp >= campaignStart && selectedTimestamp <= campaignEnd;
    const loader = show && !loadSource ? <LoadingPlaceHolder /> : null;
    const hideEmployees =
      place && place.about && place.about.settings && place.about.settings.hideEmployees ? true : false;
    const getSelectedEmployeeBox = (stickToBottom = false) => (
      <EmployeeBox
        avatarSize={stickToBottom ? 'sm' : 'md'}
        showChecked={true}
        employee={selectedEmployee}
        showNote={!isMobile()}
        showEdit={employeeCount !== 1 && !hideEmployees ? this.editEmployee : null}
      />
    );
    const originalServiceName = services && services.length > 0 ? services[0].name || '' : null;
    const lastWorkingDays = this.getLastWorkingDays();

    const servLength = (selectedEmployee.services && selectedEmployee.services.length) || 0;
    const pickedAddOn = services.filter((f) => f.about && f.about.settings && f.about.settings.isAddOn).length;

    const mainServices = services.filter((s) => !(s && s.about && s.about.settings && s.about.settings.isAddOn));

    const possibleAddOnServices =
      mainServices.length === 1 &&
      mainServices[0].extra &&
      mainServices[0].extra.addOnServices &&
      mainServices[0].extra.addOnServices.length
        ? mainServices[0].extra.addOnServices.filter(
            (s) => !serviceIsExtraTimeAddOn(s) && services.map((a) => (a ? a.id : null)).indexOf(s.id) === -1,
          )
        : [];

    const hasDealCampaign = Boolean(dealCampaign?.id);
    const showAddMoreService = !pickedAddOn && servLength > 1 && servLength > services.length && !hasDealCampaign;
    const { serviceCount } = servicesWithExtraTimeAddOns(services);

    return {
      place,
      changeTimeBookingId,
      getSelectedEmployeeBox,
      prompt,
      campaignTotal,
      cost,
      possibleAddOnServices,
      isPriceVariable,
      shouldShowPrices,
      selectedEmployee,
      pickedServicesHtml,
      loader,
      hideEmployees,
      changeEmpl,
      changeEmplModal,
      lastWorkingDays,
      originalServiceName,
      showAddMoreService,
      campaignStart,
      campaignEnd,
      hasCampaign,
      isSistaminuten,
      serviceCount,
    };
  }

  // Show picked services so far
  pickedServices(selectedEmployee) {
    const { place, services = [] } = this.props;
    const { servicesHtml, end, start } = this.selectedServices(services);
    const productsHtml = this.selectedProducts();
    const top = this.getHeader('chooseService', true);
    const result = {
      html: (
        <div className="selectEmployeeIfOned-services">
          {servicesHtml}
          {productsHtml}
          <ServicePicker
            employeeData={selectedEmployee}
            source="booking_choose_service_prompt"
            placeData={place}
            showServices={this.state.showServices}
            append={true}
            hidePopup={this.hidePopup}
            top={top}
          />
        </div>
      ),
    };

    if (start < Infinity && end > 0) {
      result.campaignStart = start;
      result.campaignEnd = end;
    }

    return result;
  }

  // Get the cost for all services
  getCost(total, isPriceVariable) {
    return (
      <span className={'inline-block'}>
        {isPriceVariable ? __('from') : __('total')}&nbsp;
        <span className="font-semibold">{total}</span>
      </span>
    );
  }

  getSteps() {
    return (
      <div className="flex justify-center">
        <ProgressTracker currentStep={2} steps={3} abortAction={this.handleAbort} abortSteps={[1]} />
      </div>
    );
  }

  employeePicker(employeeData) {
    const { changeEmplModal } = this.state;

    const top = this.getHeader('selectProfessional', true);

    return (
      <ModalDialog
        isOpen={changeEmplModal}
        appElement={document.getElementById('root')}
        onRequestClose={this.hideEmployees}>
        <ModalContent>
          <div className="sm:hidden">
            <ModalHeader onClose={this.hideEmployees} title={top} />
          </div>
          <div className="hidden sm:block">
            <div className="flex w-full justify-end">
              <span className="mr-3 mt-3">
                <Fab icon={<Icon variant="close" />} onClick={this.hideEmployees} size="md" variant="link" />
              </span>
            </div>
          </div>
          <div className="p-4">
            <EmployeePicker close={this.hideEmployees} />
          </div>
        </ModalContent>
      </ModalDialog>
    );
  }

  toggleMonthView = (e) => {
    const currentState = this.state.showMonthView || false;
    this.setState({ showMonthView: !currentState });
  };

  getMonthButton() {
    return (
      <div className="flex justify-end px-4">
        <button
          className={`cursor-pointer rounded px-1 py-1 text-center outline-none ${
            this.state.showMonthView ? 'bg-primary text-white' : 'text-primary'
          }`}
          onClick={this.toggleMonthView}>
          {__('monthView')}
        </button>
      </div>
    );
  }

  getDealCampaign() {
    return (this.props.campaigns || []).find((campaign) => campaign?.publicationType === 2);
  }

  handleAbort = (e) => {
    const isSistaminuten = Boolean(this.props.place?.lastMinuteDiscount);
    const fromLanding = Boolean(this.props.location.state?.fromLanding);
    // fromStep = entered from a booking-flow step: backing there is a rewind, not an abort.
    const fromFlowStep = Boolean(this.props.location.state?.fromStep);
    const { place, history } = this.props;

    if (!(fromLanding && fromFlowStep)) {
      bookingFlowTrackingSession.cancelSession();
    }

    if (fromLanding) {
      history.go(-1);
      return;
    }

    let pathname = '/';

    const dealCampaign = this.getDealCampaign();

    if (Boolean(dealCampaign)) {
      const { id, name } = dealCampaign;
      if (id && name) {
        pathname = ROUTE_BUILDER.dealDetails(`${slug(name)}-${id}`);
      }
    } else if (place?.id && place?.about?.slug) {
      pathname = ROUTE_BUILDER.placePage(`${place?.about?.slug}-${place?.id}`);
    }

    if (pathname) {
      history.replace({ pathname, search: isSistaminuten ? '?sistaminuten=1' : '' });
    } else {
      history.replace('/');
    }
  };

  appendSharedServices(service) {
    const { dispatch, services } = this.props;
    dispatch(bookActions.appendExtraService(service, services || []));
  }

  addSharedServices = async () => {
    const { place, services, dispatch, time, match } = this.props;

    const shared = url.returnGetParameter('shared');
    const serviceIds = url.returnGetParameter('sIds')?.split('-') || [];
    const employeeId = url.returnGetParameter('eId');
    let placeData = place;

    if (!placeData) {
      const serviceSlugId = exportIdSlug(match.params.serviceSlugId);

      const response = await server.request.get('/service/' + serviceSlugId.id + '/' + serviceSlugId.slug);
      if (response.data?.place && response.data?.service) {
        placeData = response.data?.place || undefined;
        dispatch(bookActions.addPlace(placeData));
        dispatch(bookActions.addService(response.data.service, placeData));
      }
    }

    const getSelectedEmployee = (employees) => {
      let em = { id: 0, about: { name: __('anyEmployee'), avatar: '', isBookable: true } };
      employees &&
        employees.forEach((empl) => {
          if (empl.id == employeeId) {
            em = empl;
          }
        });
      return em;
    };

    let selectedServicesIds = [];
    services &&
      services.forEach((service, key) => {
        selectedServicesIds.push(service.id);
      });

    // Runs whether or not anything is missing: the server seeds a `/share` link's whole
    // selection, so on a tab that keeps that seed there is nothing to append, but the employee
    // pick and the arrival modal are still this bootstrap's to make.
    if (placeData && shared && serviceIds?.length > 0) {
      let servicesToAdd = [];

      placeData.services.forEach((serviceGroup) => {
        serviceGroup.services.forEach((service) => {
          if (serviceIds.includes(service.id.toString()) && !selectedServicesIds.includes(service.id)) {
            servicesToAdd.push(service);

            this.appendSharedServices(service);
          }
        });
      });

      let employeeToAdd = getSelectedEmployee(placeData.employees);

      // A seeded store's picker already fetched for the whole selection; only an append changes
      // what the calendar has to show.
      if (servicesToAdd.length > 0) {
        if ((services && services[0]) || false) {
          servicesToAdd.push(services[0]);
        }

        dispatch(
          bookActions.getAvailability({
            services: servicesToAdd.filter((a) => a),
            date: time && time.timestamp,
            employee: employeeToAdd?.id || 0,
            source: 'pickEmployee',
          }),
        );
      }

      if (employeeToAdd) {
        dispatch(bookActions.pickEmployee(employeeToAdd?.id || 0, employeeToAdd.about.priceListId || 0));
      }

      shareActions.share({ placeId: placeData.id, serviceIds, employeeId }).then((result) => {
        this.setState({
          shared: {
            showModal: true,
            link: result.link,
          },
        });
      });
    }

    dispatch(loadingActions.hide());
    this.setState({ waiting: false });
  };

  hideSharedModal = (e) => {
    this.setState({ shared: { showModal: false } });
  };

  sharedModal() {
    return (
      <ModalDialog
        isOpen={this.state?.shared?.showModal}
        appElement={document.getElementById('root')}
        onRequestClose={this.hideSharedModal}>
        <ModalContent>
          <div className="mt-[34px] sm:hidden"></div>
          <div className="hidden sm:block">
            <div className="flex w-full justify-end">
              <span className="mr-3 mt-3">
                <Fab icon={<Icon variant="close" />} onClick={this.hideSharedModal} size="md" variant="link" />
              </span>
            </div>
          </div>

          <Link to="/">
            <LogoIcon className="h-[22px] w-full  text-center" />
          </Link>
          <img src="/images/share-bg.png" className="mb-[40px] mt-[40px] flex w-full" alt="bokadirekt logo" />

          {isMobile() && (
            <>
              <div className="flex flex-col text-center">
                <span className="p-[40px] pb-[25px] pt-[25px] text-lg font-bold leading-8 text-[#2A2E43]">
                  {__('share.modal.mobile_title')}
                </span>
                <div className="flex  p-[25px] text-center">
                  <a
                    className="flex w-full flex-col items-center"
                    href={this.state.shared.link}
                    onClick={(e) => this.hideSharedModal(e)}>
                    <img
                      className="mb-[25px] w-[155px]"
                      src="/images/apple_download.png"
                      alt={__('availableAppStore')}
                    />
                    <img className="w-[177px] " src="/images/google_download.png" alt={__('availablePlayStore')} />
                  </a>
                </div>
              </div>
              <div className="flex flex-col text-center">
                <a
                  className="absolute bottom-[25px] w-full"
                  href="#"
                  onClick={(e) => {
                    this.hideSharedModal(e);
                  }}>
                  <span className="p-[40px] pt-[16px] text-base font-normal italic">
                    {__('share.modal.mobile_description')}
                  </span>
                </a>
              </div>
            </>
          )}

          {!isMobile() && (
            <div className="flex flex-col text-center">
              <span className="p-[40px] pt-[20px] text-2xl font-bold leading-8 text-[#2A2E43]">
                {__('share.modal.web_title')}
              </span>
              <div className="flex h-[120px] p-[20px] ">
                <QRCode
                  size={120}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                  value={this.state.shared.link}
                  viewBox={`0 0 120 120`}
                />
              </div>
              <span className="p-[40px] pt-[16px] text-base font-normal italic text-[#2A2E43]">
                {__('share.modal.web_description')}
              </span>
            </div>
          )}
        </ModalContent>
      </ModalDialog>
    );
  }

  render() {
    if (this.state.is404) {
      // A contained surface has no marketplace chrome to fall back on.
      if (getContainmentMode() !== null) return <EntryError />;
      return <NotFound staticContext={this.props.staticContext} />;
    }
    const params = this.getParams();
    const {
      redirect,
      changeTimeBookingId,
      getSelectedEmployeeBox,
      prompt,
      shouldShowPrices,
      place,
      cost,
      changeEmpl,
      changeEmplModal,
      hideEmployees,
      pickedServicesHtml,
      loader,
      lastWorkingDays,
      showAddMoreService,
      campaignStart,
      campaignEnd,
      hasCampaign,
      selectedEmployee,
      possibleAddOnServices,
      campaignTotal,
      isSistaminuten,
      serviceCount,
    } = params;

    if (redirect) {
      return <Redirect to="/" />;
    }

    if (!isServer && (this.state.waiting || typeof this.state.waiting === 'undefined')) {
      return loader;
    }

    const changeBookingTime = Boolean(changeTimeBookingId);

    // The redesigned calendar, behind the booking-flow-redesign flag. Only the body swaps:
    // this class keeps owning bootstrap and the is404 / redirect / waiting gates above, so
    // both branches' entry behavior is identical.
    if (this.rendersNewPickTime()) {
      return (
        <PickTimePage
          place={place}
          services={this.props.services}
          employee={this.props.employee}
          time={this.props.time}
          campaignStart={campaignStart}
          campaignEnd={campaignEnd}
          lastWorkingDays={lastWorkingDays}
          isSistaminuten={isSistaminuten}
          hideEmployees={hideEmployees}
        />
      );
    }

    // The calendar can be the widget's ENTRY screen (start=time boots straight
    // here — KTD10). An entry without the in-flow fromLanding stamp has nothing
    // in-frame to go back or cancel to, so the chrome override applies exactly
    // as in BookingFlowPage: undefined keeps the marketplace Avbryt, null
    // renders no slot, { label, onBack } renders that. A fromLanding entry
    // always keeps the marketplace behaviour (handleAbort go(-1) into the
    // previous in-frame screen).
    const chromeSlot = this.props.location.state?.fromLanding ? undefined : this.props.bookingFlowChrome?.leftSlot;
    // Entered from a booking-flow step (fromStep): the button rewinds there (handleAbort),
    // so it is labelled with that step's name instead of "Avbryt", like the flow shell's
    // own back button. Anything else — modal arm, change-time, stale state — keeps Avbryt
    // (or the chrome override's label).
    const fromStep = this.props.location?.state?.fromStep;
    const stepBackLabel =
      fromStep && BOOKING_FLOW_STEP_BACK_LABEL_KEYS[fromStep]
        ? _t(BOOKING_FLOW_STEP_BACK_LABEL_KEYS[fromStep])
        : undefined;
    const backLabel = stepBackLabel ?? (chromeSlot ? chromeSlot.label : _t('abort'));
    const leftSlot =
      chromeSlot === null
        ? undefined
        : {
            button: {
              onClick: chromeSlot ? chromeSlot.onBack : this.handleAbort,
              isActive: false,
              'aria-label': backLabel,
              item: { icon: 'back', identifier: HeaderButtonIdentifier.Back },
            },
            text: backLabel,
          };

    return (
      <CheckoutViewLayout
        header={{
          leftSlot,
          middleSlot: {
            title: changeBookingTime ? __('changeTime') : __('pickTime'),
            subtitle: place?.about?.name,
          },
          rightSlot: {
            ...(place.about.profileImage && { imgUrl: place.about.profileImage }),
          },
        }}>
        <>
          {this.state?.shared?.showModal && this.sharedModal()}
          {prompt && prompt}
          <div
            className="pt-2xl md:pb-2xl md:container md:mx-auto"
            style={(() => {
              /**
               * Add margin bottom to avoid bottom sticky navbar overlap content
               */
              const bottomStickyNavbarHeight =
                this.state?.bottomStickyNavbarRefHeight || this.bottomStickyNavbarRef.current?.offsetHeight;

              if (bottomStickyNavbarHeight) {
                return { marginBottom: bottomStickyNavbarHeight };
              }
              return {};
            })()}>
            <div className="flex flex-col md:flex-row md:gap-8 lg:gap-16">
              <div className="sticky w-full md:w-7/12">
                {this.checkRetry()}
                {this.hasDiscount()}
                {!isSistaminuten && this.getMonthButton()}
                <div style={{ position: 'relative' }}>
                  {loader}
                  {!isSistaminuten && !this.state.showMonthView && (
                    <WeekPicker
                      key={this.props.availability}
                      lastWorkingDays={lastWorkingDays}
                      campaignStart={campaignStart}
                      campaignEnd={campaignEnd}
                    />
                  )}
                  {!isSistaminuten && this.state.showMonthView && (
                    <MonthPicker
                      lastWorkingDays={lastWorkingDays}
                      showWeekView={this.toggleMonthView}
                      campaignStart={campaignStart}
                      campaignEnd={campaignEnd}
                    />
                  )}
                  {isSistaminuten && <LastMinuteWeekPicker campaignStart={campaignStart} campaignEnd={campaignEnd} />}
                </div>
              </div>
              {!changeBookingTime && (
                <div className="pt-sm md:hidden">
                  {!hideEmployees && changeEmplModal && this.employeePicker(selectedEmployee)}
                  {/* Sticky Bottom Navigation bar  */}
                  <div className="fixed bottom-0 left-0 right-0 bg-white shadow">
                    <div className="px-4 pb-3 pt-2" id="bottomStickyNavbar" ref={this.bottomStickyNavbarRef}>
                      {this.state.isOpen && (
                        <Fragment>
                          <div className="border-black-100 mb-3 flex h-11 w-full items-center justify-between border-b-[1px] py-2">
                            <span className="font-semibold">{__('service', { count: serviceCount })}</span>
                            <button
                              className={`flex items-center outline-none ${
                                (themed('text-primary', 'text-black-800 underline'), isSistaminuten)
                              }`}
                              onClick={this.openBottom}>
                              <ChevronIcon className="mr-2 h-3 w-3" />
                              {__('close')}
                            </button>
                          </div>
                          {!hideEmployees && !changeEmplModal && <>{getSelectedEmployeeBox(true)}</>}
                          <div className="services pt-4">{pickedServicesHtml}</div>
                          {this.state.showRecommended && this.recommendedServices(selectedEmployee)}
                          <div className="flex flex-col items-start">
                            {possibleAddOnServices && possibleAddOnServices.length > 0 && (
                              <Button
                                variant="link"
                                onClick={this.addRecommendedServices}
                                size="sm"
                                className="!p-0 !outline-none focus:!ring-0"
                                leftIcon={
                                  <span className="bg-primary-50 rounded-full p-1">
                                    <PlusAltIcon className="text-primary h-2  w-2 " />
                                  </span>
                                }>
                                {__('recommendedServices')}
                              </Button>
                            )}
                            {showAddMoreService && (
                              <Button
                                variant="link"
                                onClick={this.addService}
                                size="sm"
                                className="!p-0 !outline-none focus:!ring-0 "
                                leftIcon={
                                  <span
                                    className={`rounded-full p-1 ${themed(
                                      'bg-primary-50',
                                      'bg-sm_primary',
                                      isSistaminuten,
                                    )}`}>
                                    <PlusAltIcon
                                      className={`h-2 w-2 ${themed('text-primary', 'text-black-800', isSistaminuten)}`}
                                    />
                                  </span>
                                }>
                                {__('addAnotherService')}
                              </Button>
                            )}
                          </div>
                          <div className="flex justify-end">{shouldShowPrices && cost}</div>
                        </Fragment>
                      )}
                      {!this.state.isOpen && (
                        <Fragment>
                          <div className="flex w-full items-center justify-between">
                            <span className="font-semibold">{__('service', { count: serviceCount })}</span>
                            <Button
                              variant="link"
                              onClick={this.openBottom}
                              size="sm"
                              className="!p-0 !outline-none focus:!ring-0"
                              leftIcon={
                                <span
                                  className={`rounded-full p-1 ${themed(
                                    'bg-primary-50',
                                    'bg-sm_primary',
                                    isSistaminuten,
                                  )}`}>
                                  <PlusAltIcon
                                    className={`h-2 w-2 ${themed('text-primary', 'text-black-800', isSistaminuten)}`}
                                  />
                                </span>
                              }>
                              {__('addOrChange')}
                            </Button>
                          </div>
                          {hasCampaign && (
                            <div className="flex flex-col items-center">
                              <span className="mb-2 font-semibold">{__('chooseTimeContinue')}</span>
                              {campaignTotal}
                            </div>
                          )}
                        </Fragment>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!changeBookingTime && (
                <div className="hidden w-5/12 md:block md:max-w-[290px]">
                  <div className="sticky top-36">
                    <div>
                      <div className="splitter font-semibold">{__('youHaveChoosen')}: </div>
                      {pickedServicesHtml}
                      <div className="flex flex-col items-baseline">
                        {possibleAddOnServices && possibleAddOnServices.length > 0 > 0 && (
                          <Button
                            variant="link"
                            onClick={this.addRecommendedServices}
                            size="sm"
                            className="!px-0 !outline-none focus:!ring-0"
                            leftIcon={
                              <span
                                className={`rounded-full p-1 ${themed(
                                  'bg-primary-50',
                                  'bg-sm_primary',
                                  isSistaminuten,
                                )}`}>
                                <PlusAltIcon
                                  className={`h-2 w-2 ${themed('text-primary', 'text-black-800', isSistaminuten)}`}
                                />
                              </span>
                            }>
                            {__('recommendedServices')}
                          </Button>
                        )}
                        {showAddMoreService && (
                          <Button
                            variant="link"
                            onClick={this.addService}
                            size="sm"
                            className="!p-0 !outline-none focus:!ring-0"
                            leftIcon={
                              <span
                                className={`rounded-full p-1 ${themed(
                                  'bg-primary-50',
                                  'bg-sm_primary',
                                  isSistaminuten,
                                )}`}>
                                <PlusAltIcon
                                  className={`h-2 w-2 ${themed('text-primary', 'text-black-800', isSistaminuten)}`}
                                />
                              </span>
                            }>
                            {__('addAnotherService')}
                          </Button>
                        )}
                      </div>
                      {!hideEmployees && <div className="py-2"></div>}
                      {this.state.showRecommended && this.recommendedServices(selectedEmployee)}
                      {hasCampaign && campaignTotal}
                      {!hideEmployees && !changeEmpl && getSelectedEmployeeBox(true)}
                      {!hideEmployees && changeEmpl && (
                        <EmployeePicker
                          noButtons={true}
                          // hide={this.hideEmployees}
                          showCheckbox={true}
                          selectedEmployee={selectedEmployee}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {this.seo()}
        </>
      </CheckoutViewLayout>
    );
  }
}

Book.contextType = LoginContext;

function mapStateToProps(state) {
  const { show, loadSource, is404 } = state.loading;
  const { services, place, employee, time, priceId, availability, campaigns, appliedBundle, changeTimeBookingId } =
    state.book;
  const { user } = state.users;
  const dealCampaign = hasDealCampagin(campaigns);

  return {
    is404,
    place,
    services,
    employee,
    availability,
    priceId,
    time,
    campaigns,
    loadSource,
    show,
    appliedBundle,
    changeTimeBookingId,
    dealCampaign,
    flags: state.flags,
    user,
  };
}

const withHistoryGoBackHandler = (Component) => {
  return (props) => {
    const history = useHistory();

    const handlePageLeave = () => {
      // The header back button cancels before its own go(-1) lands here; a second cancel
      // on a dead session is only a Sentry message.
      if (bookingFlowTrackingSession.isActive()) bookingFlowTrackingSession.cancelSession();
    };

    useEffect(() => {
      const unlisten = history.listen((newLocation, action) => {
        if (action === 'POP') {
          // A POP onto a flow step is a rewind, and onto checkout a forward redo (browser
          // forward after backing out of it) — neither leaves the booking. The same
          // direction-blind rule the step shell applies: history v4 cannot tell back from
          // forward, so a back-jump onto an older checkout entry also skips the cancel, as
          // does a back onto the calendar's own retry duplicate (checkRetry's pushState):
          // the visitor is still on the calendar, so the session stays.
          if (trackCalendarPopToStep(newLocation.pathname) || isForwardExitPathname(newLocation.pathname)) return;
          handlePageLeave();
        }
      });
      return unlisten;
    }, [history]);

    return <Component {...props} />;
  };
};

/**
 * Injects the new-picker gate as a prop so `render` can branch on it: the redesign cohort is
 * the precondition, and the weekpicker's own kill switch can take just the picker down without
 * rolling back the rest of the redesign. The cohort flag is fail-closed and the kill switch
 * fail-open, so an unresolved cohort lands on the legacy body while an unreachable Confidence
 * leaves a shipped picker up.
 *
 * `withBookingFlowRedesignFlag` cannot serve here: it redirects to the place page when the flag
 * resolves off and spins while unresolved, where this page must render the legacy body in place.
 *
 * `embedded` opts out of that gate entirely: the booking widget is outside the rollout, resolves
 * no Confidence flags at all (WidgetHost's stub client), and always serves the new picker. The
 * branch is two components rather than a conditional hook call, so neither path changes hook
 * order — and the gated one keeps every flag read out of the widget's tree.
 */
const withNewPickTime = (Component) => {
  const Gated = (props) => {
    const { enabled: redesignEnabled } = useBookingFlowRedesign();
    const { isKilled } = useWeekpickerKillSwitch();
    return <Component {...props} newPickTimeEnabled={redesignEnabled && !isKilled} />;
  };

  return ({ embedded, ...props }) => (embedded ? <Component {...props} newPickTimeEnabled /> : <Gated {...props} />);
};

// Injects the booking-flow chrome as a prop: the class already spends its one
// contextType on LoginContext, and hooks need a function boundary.
const withBookingFlowChrome = (Component) => {
  return (props) => {
    const bookingFlowChrome = useBookingFlowChrome();
    return <Component {...props} bookingFlowChrome={bookingFlowChrome} />;
  };
};

const connectedBook = withRouter(
  connect(mapStateToProps)(withHistoryGoBackHandler(withBookingFlowChrome(withNewPickTime(Book)))),
);
export default connectedBook;
